import { RequestCommunications } from "@features/cx/shared/components/communicationLandingTab";
import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import { RequestHeader } from "@features/cx/shared/components/RequestHeader";
import { RequestSummaryCards } from "@features/cx/shared/components/RequestSummaryCards";
import { RequestQuickActions } from "@features/cx/shared/components/RequestQuickActions";
import { ActivitySidebar } from "@features/cx/shared/components/ActivitySidebar";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { ServiceOrdersTab } from "@features/cx/shared/components/tabs/ServiceOrdersTab";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "@features/cx/shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Tabs } from "@shared/ui/tabs";
import {
  CheckCircle,
  Clock,
  FileText,
  InfoIcon,
  MessageSquareIcon,
  Pause,
  Play,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
import {
  useCreateServiceRequestNote,
  useServiceRequestDetail,
  useUpdateServiceRequest,
} from "../../hooks";
import ServiceDetailsTab from "./tabComponents/serviceDetailsTab";
import { formatTerritoryAddress } from "@shared/utils/address";

export function ServiceRequestDetail() {
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const { data: serviceRequestData } = useServiceRequestDetail(
    Number(id)
  );

  const [currentStatus, setCurrentStatus] = useState<
    | "pending"
    | "in progress"
    | "completed"
    | "cancelled"
    | "draft"
    | "submitted"
    | "approved"
    | "rejected"
    | "on hold"
  >("pending");

  // Update currentStatus when API data is loaded
  useEffect(() => {
    if (serviceRequestData?.result?.statusDisplay) {
      const statusFromAPI =
        serviceRequestData.result.statusDisplay.toLowerCase();
      setCurrentStatus(
        statusFromAPI as
        | "pending"
        | "in progress"
        | "completed"
        | "cancelled"
        | "draft"
        | "submitted"
        | "approved"
        | "rejected"
        | "on hold"
      );
    }
  }, [serviceRequestData]);

  const [closureRequirements, setClosureRequirements] = useState({
    customerNotification: true,
    serviceCompletion: true,
    qualityCheck: false,
    documentationUpload: true,
  });
  const { mutateAsync: updateServiceRequest, isPending: isUpdatingServiceRequest } = useUpdateServiceRequest(
    Number(id)
  );

  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const navigate = useNavigate();

  const request = serviceRequestData?.result;
  console.log("Service Request Data:", request);
  // Status change handlers
  const handleAcknowledge = () => {
    // update the status in the database
    updateServiceRequest({
      requestId: Number(id),
      status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
    });
    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS]
    );
    toast({
      title: "Success",
      description: "Service request acknowledged and moved to In Progress!",
      variant: "default",
    });
  };

  const handlePutOnHold = () => {
    // update the status in the database
    updateServiceRequest({
      requestId: Number(id),
      status: SERVICE_REQUEST_STATUS.ON_HOLD,
    });
    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.ON_HOLD]
    );
    toast({
      title: "Success",
      description: "Service request put on hold",
      variant: "default",
    });
  };

  const handleResume = () => {
    // update the status in the database
    updateServiceRequest({
      requestId: Number(id),
      status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
    });
    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS]
    );
    toast({
      title: "Success",
      description: "Service request resumed and back in progress!",
      variant: "default",
    });
  };

  const handleResolveRequest = () => {
    const {
      customerNotification,
      serviceCompletion,
      qualityCheck,
      documentationUpload,
    } = closureRequirements;

    const requiresApproval =
      serviceRequestData?.result?.utilitySupportRequest?.extraData
        ?.requiresApproval;

    const allFieldsValid =
      customerNotification &&
      serviceCompletion &&
      qualityCheck &&
      documentationUpload;

    if (requiresApproval && !allFieldsValid) {
      toast({
        title: "Error",
        description:
          "Please complete all required closure steps before resolving.",
        variant: "destructive",
      });

      return;
    }

    updateServiceRequest(
      {
        status: SERVICE_REQUEST_STATUS.COMPLETED,
        closeDate: new Date().toISOString(),
        additionalData: {
          closureRequirements: {
            customerNotification,
            serviceCompletionVerification: serviceCompletion,
            qualityCheckPending: qualityCheck,
            documentationUpload,
          },
        },
      },
      {
        onSuccess() {
          toast({
            title: "Success",
            description: "Service request resolved successfully!",
            variant: "default",
          });
          navigate("/cx/services?tab=current&page=1");
        },
      }
    );

    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.COMPLETED]
    );
  };

  const handleRejectRequest = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await updateServiceRequest(
        {
          requestId: Number(id),
          status: SERVICE_REQUEST_STATUS.REJECTED,
          closeDate: new Date().toISOString(),
          closureRemark: reason || "Service request rejected by the consumer!",
        },
        {
          onSuccess() {
            navigate("/cx/services?tab=current&page=1");
          },
        }
      );
      setCurrentStatus(
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.REJECTED]
      );
      toast({
        title: "Success",
        description: "Service request rejected successfully!",
        variant: "default",
      });
      setRejectDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject service request.",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  // Action buttons for RequestHeader
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      isLoading: isUpdatingServiceRequest,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.PENDING],
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      isLoading: isUpdatingServiceRequest,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS],
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      isLoading: isUpdatingServiceRequest,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.ON_HOLD],
    },
  ];

  // Transform API data to match RequestHeader interface
  const transformedRequestForHeader = {
    id: request?.id.toString(),
    type: request?.utilitySupportRequest.supportRequestType,
    requestNo: request?.requestNo,
    category: request?.categoryDisplay,
    subcategory: request?.subCategoryDisplay,
    subType: request?.utilitySupportRequest.supportRequestSubtype,
    name: request?.utilitySupportRequest.name,
    reason: request?.requestType,
    dateRequested: request?.createdDate,
    customerName:
      request?.consumer.firstName + " " + request?.consumer.lastName,
    accountNumber: request?.consumer.consumerNo,
    priority: request?.utilitySupportRequest?.extraData?.severityLevel || 0,
    status: currentStatus,
    isVip: request?.consumer.isVip,
    sla: {
      status: request?.slaSummary.slaStatus,
      dueDate: request?.slaSummary.slaDueDate,
      remaining: request?.slaSummary.timeRemaining,
    },
    utilitySupportRequest: {
      supportRequestType: request?.utilitySupportRequest.supportRequestType,
      supportRequestSubtype:
        request?.utilitySupportRequest.supportRequestSubtype,
    },
    priorityDisplay: request?.priorityLevelDisplay,
    statusDisplay: request?.statusDisplay,
  };

  // Transform API data to match RequestSummaryCards interface
  const transformedRequestForSummary = {
    action: "service",
    id: request?.id.toString(),
    requestNo: request?.requestNo.toString(),
    dateRequested: request?.createdDate,
    requestedBy: request?.createdUserRemoteName,
    assignedTo: request?.assignedTo || "Unassigned",
    lastUpdatedOn: request?.lastModifiedDate,
    lastUpdatedBy: request?.lastModifiedUserRemoteName,
    resolvedBy:request?.resolvedBy,
    resolvedOn:request?.closeDate,
    customer: {
      consumerNo: request?.consumer.consumerNo,
      id: request?.consumer.id?.toString(),
      firstName: request?.consumer.firstName,
      lastName: request?.consumer.lastName,
      phone: request?.consumer.contactNumber,
      email: request?.consumer.email,
      category: request?.consumer.category,
      subCategory: request?.consumer.subCategory,
      categoryDisplay: request?.categoryDisplay,
      subCategoryDisplay: request?.subCategoryDisplay,
      serviceAddress: formatTerritoryAddress(
        { service: request?.consumer.consumerAddress.service.hierarchyNames },
        "service"
      ) || "No address configured",
      billingAddress: formatTerritoryAddress(
        { billing: request?.consumer.consumerAddress.billing.hierarchyNames },
        "billing"
      ) || "No address configured",
      ownershipDisplay:request?.consumer?.ownershipDisplay || "NA",
      masterAccountNo: request?.consumer?.ownershipDetails?.approvalDetails?.masterAccountNo || "NA",
    },
  };

  // Simplified request for QuickActions
  const requestForQuickActions = {
    id: request?.id.toString() || "",
    requestNo: request?.requestNo || "",
    customer: {
      id: request?.consumer.id?.toString() || "NA",
      firstName: request?.consumer.firstName || "NA",
      lastName: request?.consumer.lastName || "NA",
      phone: request?.consumer.contactNumber || "NA",
      email: request?.consumer.email || "NA",
      consumerNo: request?.consumer.consumerNo || "NA",
    }
  };

  return (
    <div className="space-y-4">
      {/* Main Content Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        <div className="lg:col-span-3 space-y-6">
          <RequestHeader
            request={transformedRequestForHeader}
            currentStatus={currentStatus.toLowerCase()}
            actionButtons={actionButtons}
            action="Service"
          >
            <RequestSummaryCards
              request={transformedRequestForSummary}
            />

            {/* Configurable Tabs Section */}
            <div className="mt-6 border-t pt-4">
              <Tabs
                className="w-full"
                tabsListClassName="grid w-full grid-cols-5 mb-6"
                defaultValue="details"
                urlMapping={{
                  details: "details",
                  communication: "communication",
                  serviceOrders: "service-orders",
                }}
                tabComponents={{
                  details: {
                    label: "Details",
                    icon: <InfoIcon className="h-4 w-4" />,
                    component: (
                      <ServiceDetailsTab
                        request={request}
                        remoteUtilityId={Number(remoteUtilityId)}
                        onResolve={handleResolveRequest}
                        onReject={handleRejectRequest}
                        closureRequirements={closureRequirements}
                        setClosureRequirements={setClosureRequirements}
                        isResolving={isUpdatingServiceRequest}
                      />
                    ),
                  },
                  serviceOrders: {
                    label: "Service Orders",
                    icon: <CheckCircle className="h-4 w-4" />,
                    component: (
                      <ServiceOrdersTab
                        consumerAccountNumber={request?.consumer.consumerNo || ""}
                        consumerId={request?.consumer.id?.toString() || ""}
                        consumerName={
                          request?.consumer.firstName +
                          " " +
                          request?.consumer.lastName
                        }
                        requestId={request?.id?.toString() || ""}
                        remoteUtilityId={Number(remoteUtilityId) || 0}
                        currentStatus={request?.statusDisplay || currentStatus}
                      />
                    ),
                  },
                  communication: {
                    label: "Communication",
                    icon: <MessageSquareIcon className="h-4 w-4" />,
                    subTabsListClassName: "grid w-full grid-cols-3",
                    component: (
                      <RequestCommunications
                        entityId={request?.id.toString()}
                        remoteUtilityId={Number(remoteUtilityId)}
                        email={request?.consumer.email}
                        phone={request?.consumer.contactNumber || ""}
                        action="consumer_support_request"
                      />
                    ),
                  },
                  notes: {
                    label: "Notes",
                    icon: <FileText className="h-4 w-4" />,
                    component: (
                      <NotesTab
                        source={null}
                        requestId={request?.id.toString()}
                        remoteUtilityId={Number(remoteUtilityId)}
                      />
                    ),
                  },
                  timeLine: {
                    label: "Time Line",
                    icon: <Clock className="h-4 w-4" />,
                    component: (
                      <TimelineTab
                        remoteUtilityId={Number(remoteUtilityId)}
                        entityId={request?.id.toString()}
                        actionType="service"
                      />
                    ),
                  },
                }}
              />
            </div>
          </RequestHeader>
        </div>

        {/* Right Column (1/4): Quick Actions and Activity Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <RequestQuickActions
            request={requestForQuickActions}
            currentStatus={currentStatus}
            actionButtons={actionButtons}
            handleResolveRequest={handleResolveRequest}
            handleRejectRequest={handleRejectRequest}
          />
          <ActivitySidebar
            entityId={request?.id?.toString() || ""}
            remoteUtilityId={Number(remoteUtilityId) || 0}
            actionType="service"
          />
        </div>
      </div>

      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject Service Request"
        description="Please provide a reason for rejecting this service request."
        label="Rejection Reason"
        placeholder="Please provide a reason for rejecting this request..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
}
